import React, { InputHTMLAttributes } from 'react';
import { MagnifyingGlassIcon } from '@/components/Icons';
import cn from '@/utils/cn';

interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  value: string;
  onChange: (value: string) => void;
  /** Classes for the outer wrapper (width/sizing/shrink) - the input itself always fills it. */
  containerClassName?: string;
}

/**
 * Shared search box: magnifying-glass icon + rounded pill input. Used by Team Members, the
 * favorite-accounts picker, and (soon) the cards table - pulled out here once a third consumer
 * needed the exact same look and behaviour, rather than re-copying the same JSX a third time.
 */
const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  containerClassName,
  className,
  placeholder,
  'aria-label': ariaLabel,
  ...inputProps
}) => (
  <div className={cn('relative', containerClassName)}>
    <MagnifyingGlassIcon
      size="xs"
      color="navy"
      className="absolute left-3 top-1/2 -translate-y-1/2 text-emerge-navy-2"
    />
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      // Placeholder text disappears the moment there's a value, so it's not a persistent
      // accessible name - callers should pass their own aria-label, but this falls back to the
      // placeholder rather than leaving the field unlabelled if one is missed.
      aria-label={ariaLabel ?? placeholder}
      autoComplete="off"
      className={cn(
        'w-full rounded-full border border-emerge-navy-4 bg-white py-2 pl-9 pr-4 text-sm text-emerge-navy placeholder:text-emerge-navy-3 focus:outline-none focus:ring-1 focus:ring-emerge-teal',
        className
      )}
      {...inputProps}
    />
  </div>
);

export default SearchInput;
